import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './AuthContext'

const ConversationsContext = createContext()

export function ConversationsProvider({ children }) {
  const { user } = useAuth()
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const sortMessages = (messages) => {
    return [...messages].sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    )
  }

  // Load conversations for the current user
  const loadConversations = async () => {
    try {
      setLoading(true)
      
      // If no user is logged in, don't load any conversations
      if (!user) {
        setConversations([])
        return
      }
      
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          *,
          messages (*)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error

      const sortedData = data?.map(conv => ({
        ...conv,
        messages: sortMessages(conv.messages || [])
      })) || []

      setConversations(sortedData)
    } catch (error) {
      console.error('Error fetching conversations:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  // Refresh a single conversation
  const refreshConversation = async (conversationId) => {
    try {
      if (!user) return
      
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          *,
          messages (*)
        `)
        .eq('id', conversationId)
        .eq('user_id', user.id) // Ensure the conversation belongs to this user
        .single()

      if (error) throw error

      setConversations(current => {
        const index = current.findIndex(c => c.id === conversationId)
        const updatedConversation = {
          ...data,
          messages: sortMessages(data.messages || [])
        }

        if (index === -1) {
          return [updatedConversation, ...current]
        }

        const newConversations = [...current]
        newConversations[index] = updatedConversation
        return [...newConversations]
      })
    } catch (error) {
      console.error('Error refreshing conversation:', error)
    }
  }

  // Initial load and subscription setup
  useEffect(() => {
    // Load conversations when the user changes
    loadConversations()
    
    // Don't set up subscriptions if no user is logged in
    if (!user) return

    // Subscribe to conversation changes for this user
    const conversationsChannel = supabase
      .channel(`conversations-changes-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `user_id=eq.${user.id}` // Only subscribe to this user's conversations
        },
        async (payload) => {
          console.log('Conversation change detected:', payload)
          
          if (payload.eventType === 'DELETE') {
            // Remove deleted conversation
            setConversations(current => 
              current.filter(conv => conv.id !== payload.old.id)
            )
          } else {
            // For INSERT or UPDATE, fetch the full conversation with messages
            try {
              const { data, error } = await supabase
                .from('conversations')
                .select(`
                  *,
                  messages (*)
                `)
                .eq('id', payload.new.id)
                .eq('user_id', user.id) // Ensure the conversation belongs to this user
                .single()
              
              if (error) throw error
              
              setConversations(current => {
                const index = current.findIndex(c => c.id === payload.new.id)
                const updatedConversation = {
                  ...data,
                  messages: sortMessages(data.messages || [])
                }
                
                // For new conversations
                if (index === -1) {
                  return [updatedConversation, ...current]
                }
                
                // For updated conversations
                const newConversations = [...current]
                newConversations[index] = updatedConversation
                return [...newConversations]
              })
            } catch (error) {
              console.error('Error handling conversation change:', error)
            }
          }
        }
      )
      .subscribe()

    return () => {
      conversationsChannel.unsubscribe()
    }
  }, [user]) // Re-run when the user changes

  return (
    <ConversationsContext.Provider value={{ 
      conversations, 
      loading, 
      error,
      refreshConversation,
      reloadConversations: loadConversations
    }}>
      {children}
    </ConversationsContext.Provider>
  )
}

export const useConversations = () => useContext(ConversationsContext) 